import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, TrendingUp, TrendingDown, FileText, Calendar, CheckCircle } from 'lucide-react';
import { Wallet, Lightning, ChartLine, Receipt, House, Drop, Fire, Users, Scales, Wrench, DotsThree, Truck, Carrot } from '@phosphor-icons/react';
import { expensesAPI, staffAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

const CATEGORY_ICONS = {
  'KIRA': House,
  'ELEKTRIK': Lightning,
  'SU': Drop,
  'DOGALGAZ': Fire,
  'TUP': Fire,
  'KURYE_MOTOR': Truck,
  'MANAV': Carrot,
  'MAAS': Users,
  'SGK_SIGORTA': Scales,
  'VERGI': Receipt,
  'KDV': Receipt,
  'BAKIM_ONARIM': Wrench,
  'DIGER': DotsThree,
};

const CATEGORY_COLORS = {
  'Kira': '#8884d8',
  'Elektrik': '#ffc658',
  'Su': '#82ca9d',
  'Dogalgaz/Kömür': '#ff8042',
  'Tüp': '#ff6b6b',
  'Kurye/Motor': '#9b59b6',
  'Manav/Sebze': '#27ae60',
  'Personel Maaşları': '#0088FE',
  'SGK/Sigorta': '#00C49F',
  'Vergi': '#FFBB28',
  'KDV': '#FF8042',
  'Bakım/Onarım': '#8dd1e1',
  'Diğer': '#a4a4a4',
};

export default function Expenses() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [expenses, setExpenses] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  
  // Günlük görünüm için state
  const [selectedDate, setSelectedDate] = useState(() => {
    return new Date().toISOString().split('T')[0]; // Bugünün tarihi
  });
  const [dailyExpenses, setDailyExpenses] = useState([]);
  const [dailyTotal, setDailyTotal] = useState(0);
  const [cumulativeTotal, setCumulativeTotal] = useState(0); // Seçilen tarihe kadar kümülatif toplam
  
  const [isExpenseDialogOpen, setIsExpenseDialogOpen] = useState(false);
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  
  const [expenseForm, setExpenseForm] = useState({
    category: '',
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    is_recurring: false,
    payment_type: 'cash',
  });
  
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [newCategory, setNewCategory] = useState({ code: '', label: '' });
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('');
  
  // Tüp Faturası Dialog
  const [isTupDialogOpen, setIsTupDialogOpen] = useState(false);
  const [tupForm, setTupForm] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: language === 'tr' ? 'Sanayi Tüpü' : 'Industrial Gas Cylinder',
  });
  
  const PAYMENT_TYPES = [
    { code: 'cash', label: t('expenses.cash') },
    { code: 'credit_card', label: t('expenses.creditCard') },
    { code: 'bank_transfer', label: t('expenses.bankTransfer') },
    { code: 'check', label: t('expenses.check') },
  ];
  
  const [templateForm, setTemplateForm] = useState({
    category: '',
    amount: '',
    description: '',
  });

  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager' || isAdmin;

  useEffect(() => {
    fetchData();
    checkReminder();
  }, [selectedMonth]);

  // Seçilen tarihe göre günlük giderleri filtrele
  useEffect(() => {
    filterDailyExpenses();
  }, [expenses, selectedDate]);

  const filterDailyExpenses = () => {
    // Seçilen güne ait giderleri filtrele
    const dayExpenses = expenses.filter(exp => {
      // Günlük giderler: sadece o günün tarihi eşleşmeli
      if (!exp.is_recurring) {
        return exp.date === selectedDate;
      }
      // Sabit giderler: seçilen ay içinde olmalı (günlük olarak /30 hesaplanır)
      const expMonth = exp.date.substring(0, 7);
      const selectedMonthStr = selectedDate.substring(0, 7);
      return expMonth === selectedMonthStr;
    });
    
    // Günlük toplam hesapla
    let total = 0;
    dayExpenses.forEach(exp => {
      if (exp.is_recurring) {
        total += exp.amount / 30; // Sabit giderler günlük olarak bölünür
      } else {
        total += exp.amount;
      }
    });
    
    // Seçilen tarihe kadar kümülatif toplam hesapla
    let cumulative = 0;
    const selectedDateObj = new Date(selectedDate);
    const monthStart = new Date(selectedDate.substring(0, 7) + '-01');
    const dayOfMonth = selectedDateObj.getDate();
    
    expenses.forEach(exp => {
      const expDate = new Date(exp.date);
      if (exp.is_recurring) {
        // Sabit giderler: seçilen güne kadar olan günler * günlük tutar
        cumulative += (exp.amount / 30) * dayOfMonth;
      } else {
        // Günlük giderler: sadece seçilen tarih veya öncesi
        if (expDate <= selectedDateObj) {
          cumulative += exp.amount;
        }
      }
    });
    
    setDailyExpenses(dayExpenses);
    setDailyTotal(total);
    setCumulativeTotal(cumulative);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [expensesRes, templatesRes, categoriesRes, summaryRes] = await Promise.all([
        expensesAPI.getAll({ month: selectedMonth }),
        expensesAPI.getTemplates(),
        expensesAPI.getCategories(),
        expensesAPI.getSummary(),
      ]);
      setExpenses(expensesRes.data);
      setTemplates(templatesRes.data);
      setCategories(categoriesRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const checkReminder = async () => {
    try {
      const res = await expensesAPI.getReminder();
      if (res.data.show_reminder && res.data.missing_count > 0) {
        // Sadece bu ay seçiliyse hatırlatıcı göster
        const now = new Date();
        const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        if (selectedMonth === currentMonth) {
          toast.info(
            `${res.data.missing_count} sabit gider şablonu uygulanmadı. "Şablonları Uygula" butonunu kullanabilirsiniz.`,
            { duration: 5000 }
          );
        }
      }
    } catch (error) {
      console.error('Hatırlatıcı kontrol edilemedi');
    }
  };

  const handleCreateExpense = async () => {
    if (!expenseForm.category || !expenseForm.amount) {
      toast.error('Kategori ve tutar gerekli');
      return;
    }

    try {
      if (editingExpense) {
        await expensesAPI.update(editingExpense.id, {
          ...expenseForm,
          amount: parseFloat(expenseForm.amount),
        });
        toast.success('Gider güncellendi');
      } else {
        await expensesAPI.create({
          ...expenseForm,
          amount: parseFloat(expenseForm.amount),
        });
        
        // Eğer farklı bir aya gider eklendiyse, o aya geç
        const expenseMonth = expenseForm.date.substring(0, 7);
        if (expenseMonth !== selectedMonth) {
          setSelectedMonth(expenseMonth);
          setSelectedDate(expenseForm.date);
          toast.success(`Gider ${expenseForm.date} tarihine eklendi. Sayfa ${expenseMonth} ayına yönlendirildi.`, { duration: 4000 });
        } else {
          toast.success('Gider eklendi');
        }
      }
      setIsExpenseDialogOpen(false);
      resetExpenseForm();
      // fetchData() artık useEffect içinde selectedMonth değiştiğinde otomatik çağrılacak
      if (expenseForm.date.substring(0, 7) === selectedMonth) {
        fetchData();
      }
    } catch (error) {
      console.error('Gider ekleme hatası:', error);
      toast.error(error.response?.data?.detail || 'İşlem başarısız: ' + (error.message || 'Bilinmeyen hata'));
    }
  };

  const handleDeleteExpense = async (id) => {
    if (!confirm('Bu gideri silmek istediğinize emin misiniz?')) return;

    try {
      await expensesAPI.delete(id);
      toast.success('Gider silindi');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Silme başarısız');
    }
  };

  // Tüp Faturası - Günlere Bölerek Ekle
  const handleAddTupFatura = async () => {
    if (!tupForm.amount || !tupForm.date) {
      toast.error('Tutar ve tarih gerekli');
      return;
    }

    try {
      const faturaDate = new Date(tupForm.date);
      const dayOfMonth = faturaDate.getDate(); // Ayın kaçıncı günü
      const totalAmount = parseFloat(tupForm.amount);
      const dailyAmount = totalAmount / dayOfMonth; // Günlük tutar
      
      // Ay içinde zaten tüp kaydı var mı kontrol et
      const monthStr = tupForm.date.substring(0, 7);
      const existingTup = expenses.filter(e => 
        e.category === 'TUP' && 
        e.date.startsWith(monthStr) &&
        e.description?.includes('Sanayi Tüpü')
      );
      
      if (existingTup.length > 0) {
        const confirmAdd = confirm(
          `Bu ay için zaten ${existingTup.length} adet tüp kaydı var.\n\n` +
          `Yine de eklemek istiyor musunuz?\n` +
          `(Mükerrer kayıt oluşabilir)`
        );
        if (!confirmAdd) return;
      }
      
      // 1'den bugüne kadar her gün için gider oluştur
      const year = faturaDate.getFullYear();
      const month = faturaDate.getMonth();
      
      let successCount = 0;
      
      for (let day = 1; day <= dayOfMonth; day++) {
        const date = new Date(year, month, day);
        const dateStr = date.toISOString().split('T')[0];
        
        try {
          await expensesAPI.create({
            category: 'TUP',
            amount: parseFloat(dailyAmount.toFixed(2)),
            description: `${tupForm.description} (${day}/${dayOfMonth} - Toplam: ₺${totalAmount.toLocaleString('tr-TR')})`,
            date: dateStr,
            is_recurring: false,
            payment_type: 'credit_card',
          });
          successCount++;
        } catch (err) {
          console.error(`${dateStr} için gider eklenemedi:`, err);
        }
      }
      
      toast.success(
        `Tüp faturası ${dayOfMonth} güne bölündü! ` +
        `Günlük: ₺${dailyAmount.toFixed(2)} × ${dayOfMonth} gün = ₺${totalAmount.toLocaleString('tr-TR')}`,
        { duration: 5000 }
      );
      
      setIsTupDialogOpen(false);
      setTupForm({
        amount: '',
        date: new Date().toISOString().split('T')[0],
        description: 'Sanayi Tüpü',
      });
      fetchData();
    } catch (error) {
      toast.error('Tüp faturası eklenirken hata oluştu');
    }
  };

  const handleCreateTemplate = async () => {
    if (!templateForm.category || !templateForm.amount) {
      toast.error('Kategori ve tutar gerekli');
      return;
    }

    try {
      await expensesAPI.createTemplate({
        ...templateForm,
        amount: parseFloat(templateForm.amount),
      });
      toast.success('Şablon oluşturuldu');
      setIsTemplateDialogOpen(false);
      setTemplateForm({ category: '', amount: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Şablon oluşturulamadı');
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!confirm('Bu şablonu silmek istediğinize emin misiniz?')) return;

    try {
      await expensesAPI.deleteTemplate(id);
      toast.success('Şablon silindi');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Silme başarısız');
    }
  };

  const handleApplyTemplates = async () => {
    try {
      const res = await expensesAPI.applyTemplates(selectedMonth);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Şablonlar uygulanamadı');
    }
  };

  const handleTransferSalaries = async () => {
    try {
      const res = await staffAPI.transferSalaries(selectedMonth);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Maaşlar aktarılamadı');
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategory.code || !newCategory.label) {
      toast.error('Kategori kodu ve adı gerekli');
      return;
    }

    try {
      await expensesAPI.createCategory(newCategory);
      toast.success('Kategori eklendi');
      setIsCategoryDialogOpen(false);
      setNewCategory({ code: '', label: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kategori eklenemedi');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!confirm('Bu kategoriyi silmek istediğinize emin misiniz?')) return;

    try {
      await expensesAPI.deleteCategory(id);
      toast.success('Kategori silindi');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kategori silinemedi');
    }
  };

  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setExpenseForm({
      category: expense.category,
      amount: expense.amount.toString(),
      description: expense.description || '',
      date: expense.date,
      is_recurring: expense.is_recurring,
      payment_type: expense.payment_type || 'cash',
    });
    setIsExpenseDialogOpen(true);
  };

  const resetExpenseForm = () => {
    setEditingExpense(null);
    setExpenseForm({
      category: '',
      amount: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      is_recurring: false,
      payment_type: 'cash',
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(value);
  };

  const monthTotal = expenses.reduce((sum, e) => sum + e.amount, 0);
  const recurringTotal = expenses.filter(e => e.is_recurring).reduce((sum, e) => sum + e.amount, 0);
  const nonRecurringTotal = expenses.filter(e => !e.is_recurring).reduce((sum, e) => sum + e.amount, 0);
  const dailyAverage = recurringTotal / 30;

  // Prepare chart data
  const pieData = summary?.by_category
    ? Object.entries(summary.by_category).map(([name, value]) => ({
        name,
        value,
        color: CATEGORY_COLORS[name] || '#a4a4a4',
      }))
    : [];

  const barData = summary?.monthly_history?.slice().reverse() || [];

  // Generate month options
  const monthOptions = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = date.toLocaleDateString('tr-TR', { year: 'numeric', month: 'long' });
    monthOptions.push({ value, label });
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold tracking-tight">{t('expenses.title')}</h1>
            <p className="text-muted-foreground mt-1">{t('expenses.subtitle')}</p>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-48" data-testid="month-select">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {isManager && (
              <>
                <Button variant="outline" onClick={handleApplyTemplates} data-testid="apply-templates-btn">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {t('expenses.applyTemplates')}
                </Button>
                
                {isAdmin && (
                  <Button variant="outline" onClick={handleTransferSalaries} data-testid="transfer-salaries-btn">
                    <Users className="w-4 h-4 mr-2" />
                    {t('expenses.transferSalaries')}
                  </Button>
                )}
                
                {/* Tüp Faturası Ekle Butonu */}
                <Dialog open={isTupDialogOpen} onOpenChange={setIsTupDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="bg-orange-50 border-orange-300 text-orange-700 hover:bg-orange-100" data-testid="add-tup-btn">
                      <Fire className="w-4 h-4 mr-2" />
                      {t('expenses.gasInvoice')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Fire className="w-5 h-5 text-orange-600" />
                        Sanayi Tüpü Faturası
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                        <p className="text-sm text-orange-800">
                          <strong>Nasıl Çalışır?</strong><br/>
                          Fatura tutarı, ayın 1'inden seçilen tarihe kadar olan günlere eşit olarak bölünür.
                        </p>
                        <p className="text-xs text-orange-600 mt-1">
                          Örnek: 21 Nisan'da ₺630 → Her güne ₺30 (21 gün)
                        </p>
                      </div>
                      
                      <div>
                        <Label>Fatura Tarihi</Label>
                        <Input
                          type="date"
                          value={tupForm.date}
                          onChange={(e) => setTupForm({ ...tupForm, date: e.target.value })}
                        />
                        {tupForm.date && (
                          <p className="text-xs text-muted-foreground mt-1">
                            1 - {new Date(tupForm.date).getDate()} {new Date(tupForm.date).toLocaleDateString('tr-TR', { month: 'long' })} 
                            ({new Date(tupForm.date).getDate()} gün)
                          </p>
                        )}
                      </div>
                      
                      <div>
                        <Label>Fatura Tutarı (₺)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={tupForm.amount}
                          onChange={(e) => setTupForm({ ...tupForm, amount: e.target.value })}
                          placeholder="630"
                        />
                        {tupForm.amount && tupForm.date && (
                          <p className="text-sm text-green-600 mt-1 font-medium">
                            Günlük: ₺{(parseFloat(tupForm.amount) / new Date(tupForm.date).getDate()).toFixed(2)}
                          </p>
                        )}
                      </div>
                      
                      <div>
                        <Label>Açıklama</Label>
                        <Input
                          value={tupForm.description}
                          onChange={(e) => setTupForm({ ...tupForm, description: e.target.value })}
                          placeholder="Sanayi Tüpü"
                        />
                      </div>
                      
                      <div className="pt-2 border-t">
                        <div className="flex justify-between text-sm mb-2">
                          <span>Toplam Tutar:</span>
                          <span className="font-bold">₺{parseFloat(tupForm.amount || 0).toLocaleString('tr-TR')}</span>
                        </div>
                        <div className="flex justify-between text-sm mb-2">
                          <span>Gün Sayısı:</span>
                          <span>{tupForm.date ? new Date(tupForm.date).getDate() : '-'} gün</span>
                        </div>
                        <div className="flex justify-between text-sm text-green-600 font-medium">
                          <span>Günlük Gider:</span>
                          <span>
                            ₺{tupForm.amount && tupForm.date 
                              ? (parseFloat(tupForm.amount) / new Date(tupForm.date).getDate()).toFixed(2) 
                              : '0.00'}
                          </span>
                        </div>
                      </div>
                      
                      <Button onClick={handleAddTupFatura} className="w-full bg-orange-600 hover:bg-orange-700">
                        <Fire className="w-4 h-4 mr-2" />
                        Günlere Bölerek Ekle
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
                
                <Dialog open={isExpenseDialogOpen} onOpenChange={(open) => {
                  setIsExpenseDialogOpen(open);
                  if (!open) resetExpenseForm();
                }}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-expense-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Gider Ekle
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>{editingExpense ? 'Gider Düzenle' : 'Yeni Gider'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      {/* Gider Tipi Seçimi */}
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => setExpenseForm({ ...expenseForm, is_recurring: false })}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            !expenseForm.is_recurring 
                              ? 'border-primary bg-primary/10' 
                              : 'border-border hover:border-muted-foreground'
                          }`}
                        >
                          <p className="font-medium text-sm">Günlük Gider</p>
                          <p className="text-xs text-muted-foreground">O güne özel (tamir, acil alım)</p>
                        </button>
                        <button
                          type="button"
                          onClick={() => setExpenseForm({ ...expenseForm, is_recurring: true })}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            expenseForm.is_recurring 
                              ? 'border-primary bg-primary/10' 
                              : 'border-border hover:border-muted-foreground'
                          }`}
                        >
                          <p className="font-medium text-sm">Sabit Gider</p>
                          <p className="text-xs text-muted-foreground">Aylık (kira, elektrik)</p>
                        </button>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <Label>Kategori</Label>
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="sm"
                            className="text-xs h-6"
                            onClick={() => setIsCategoryDialogOpen(true)}
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Yeni Kategori
                          </Button>
                        </div>
                        <Select value={expenseForm.category} onValueChange={(v) => setExpenseForm({ ...expenseForm, category: v })}>
                          <SelectTrigger data-testid="expense-category-select">
                            <SelectValue placeholder="Kategori seçin" />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map((cat) => (
                              <SelectItem key={cat.code} value={cat.code}>
                                {cat.label} {!cat.is_default && <span className="text-xs text-muted-foreground">(Özel)</span>}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label>Tutar (TL)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={expenseForm.amount}
                          onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                          placeholder="0.00"
                          data-testid="expense-amount-input"
                        />
                      </div>
                      
                      <div>
                        <Label>{expenseForm.is_recurring ? 'Ay Başlangıç Tarihi' : 'Gider Tarihi'}</Label>
                        <Input
                          type="date"
                          value={expenseForm.date}
                          onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                          data-testid="expense-date-input"
                        />
                        {!expenseForm.is_recurring && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Bu gider sadece seçilen günün raporunda görünür
                          </p>
                        )}
                        {expenseForm.is_recurring && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Sabit giderler 30'a bölünerek günlük rapora yansır
                          </p>
                        )}
                      </div>
                      
                      <div>
                        <Label>Ödeme Türü</Label>
                        <Select value={expenseForm.payment_type} onValueChange={(v) => setExpenseForm({ ...expenseForm, payment_type: v })}>
                          <SelectTrigger data-testid="expense-payment-type-select">
                            <SelectValue placeholder="Ödeme türü seçin" />
                          </SelectTrigger>
                          <SelectContent>
                            {PAYMENT_TYPES.map((pt) => (
                              <SelectItem key={pt.code} value={pt.code}>
                                {pt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label>Açıklama (Opsiyonel)</Label>
                        <Input
                          value={expenseForm.description}
                          onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                          placeholder="Açıklama..."
                        />
                      </div>
                      
                      <Button onClick={handleCreateExpense} className="w-full" data-testid="save-expense-btn">
                        {editingExpense ? 'Güncelle' : 'Kaydet'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-red-500/10 rounded-lg shrink-0">
                    <Wallet className="w-4 h-4 text-red-500" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground uppercase truncate">Bu Ay Toplam</p>
                    <p className="text-lg font-bold truncate">{formatCurrency(summary.current_month_total)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-primary/30 bg-primary/5">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-primary/20 rounded-lg shrink-0">
                    <Calendar className="w-4 h-4 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground uppercase truncate">
                      {selectedDate === new Date().toISOString().split('T')[0] ? 'Bugüne Kadar' : `${new Date(selectedDate).getDate()}. Güne Kadar`}
                    </p>
                    <p className="text-lg font-bold text-primary truncate">{formatCurrency(cumulativeTotal)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-blue-500/10 rounded-lg shrink-0">
                    <Receipt className="w-4 h-4 text-blue-500" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground uppercase truncate">Sabit Giderler</p>
                    <p className="text-lg font-bold truncate">{formatCurrency(recurringTotal)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-orange-500/10 rounded-lg shrink-0">
                    <Lightning className="w-4 h-4 text-orange-500" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground uppercase truncate">Günlük Giderler</p>
                    <p className="text-lg font-bold truncate">{formatCurrency(dailyTotal)}</p>
                    <p className="text-xs text-muted-foreground">bugün {dailyExpenses.length} işlem</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-purple-500/10 rounded-lg shrink-0">
                    <FileText className="w-4 h-4 text-purple-500" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground uppercase truncate">Gider Sayısı</p>
                    <p className="text-lg font-bold">{expenses.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* GÜNLÜK GİDERLER BÖLÜMÜ - ANA EKRAN */}
        <Card className="border-2 border-primary/20 bg-gradient-to-r from-primary/5 to-transparent">
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle className="text-xl flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  Günlük Giderler
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {selectedDate === new Date().toISOString().split('T')[0] 
                    ? 'Bugünün giderleri' 
                    : `${new Date(selectedDate).toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} giderleri`
                  }
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Label className="text-sm whitespace-nowrap">Tarih:</Label>
                  <Input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-40"
                    data-testid="daily-date-picker"
                  />
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
                  className={selectedDate === new Date().toISOString().split('T')[0] ? 'bg-primary/10' : ''}
                >
                  Bugün
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Günlük Özet Kartları */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-background rounded-lg p-3 border">
                <p className="text-xs text-muted-foreground">Günlük Toplam</p>
                <p className="text-xl font-bold text-red-600">{formatCurrency(dailyTotal)}</p>
              </div>
              <div className="bg-background rounded-lg p-3 border">
                <p className="text-xs text-muted-foreground">Günlük Gider</p>
                <p className="text-xl font-bold text-orange-600">
                  {formatCurrency(dailyExpenses.filter(e => !e.is_recurring).reduce((sum, e) => sum + e.amount, 0))}
                </p>
              </div>
              <div className="bg-background rounded-lg p-3 border">
                <p className="text-xs text-muted-foreground">Sabit Gider (Günlük)</p>
                <p className="text-xl font-bold text-blue-600">
                  {formatCurrency(dailyExpenses.filter(e => e.is_recurring).reduce((sum, e) => sum + (e.amount / 30), 0))}
                </p>
              </div>
              <div className="bg-background rounded-lg p-3 border">
                <p className="text-xs text-muted-foreground">Gider Kalemi</p>
                <p className="text-xl font-bold">{dailyExpenses.length}</p>
              </div>
            </div>

            {/* Günlük Gider Listesi */}
            {dailyExpenses.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground bg-muted/30 rounded-lg">
                <Calendar className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>Bu tarihte gider kaydı yok</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Kategori</TableHead>
                    <TableHead>Açıklama</TableHead>
                    <TableHead>Tür</TableHead>
                    <TableHead>Ödeme</TableHead>
                    <TableHead className="text-right">Tutar</TableHead>
                    <TableHead className="text-right">Günlük Etki</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dailyExpenses.map((expense) => {
                    const Icon = CATEGORY_ICONS[expense.category] || DotsThree;
                    const dailyImpact = expense.is_recurring ? expense.amount / 30 : expense.amount;
                    return (
                      <TableRow key={expense.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4 text-muted-foreground" />
                            <span>{expense.category_label}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {expense.description || '-'}
                        </TableCell>
                        <TableCell>
                          {expense.is_recurring ? (
                            <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-600 border-blue-500/20">Sabit</Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs bg-orange-500/10 text-orange-600 border-orange-500/20">Günlük</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {expense.payment_type_label || 'Nakit'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(expense.amount)}
                          {expense.is_recurring && <span className="text-xs text-muted-foreground">/ay</span>}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-red-600">
                          {formatCurrency(dailyImpact)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow className="bg-muted/50 font-bold">
                    <TableCell colSpan={5}>GÜNLÜK TOPLAM</TableCell>
                    <TableCell className="text-right text-red-600">{formatCurrency(dailyTotal)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Tabs - Aylık Detaylar */}
        <Tabs defaultValue="list" className="space-y-6">
          <TabsList>
            <TabsTrigger value="list" data-testid="tab-expense-list">Aylık Gider Listesi</TabsTrigger>
            <TabsTrigger value="charts" data-testid="tab-expense-charts">Grafikler</TabsTrigger>
            <TabsTrigger value="templates" data-testid="tab-expense-templates">Şablonlar</TabsTrigger>
          </TabsList>

          {/* Expense List Tab */}
          <TabsContent value="list">
            <Card>
              <CardHeader>
                <CardTitle>
                  {monthOptions.find(m => m.value === selectedMonth)?.label} Giderleri
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : expenses.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Bu ay için gider kaydı yok
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Kategori</TableHead>
                        <TableHead>Açıklama</TableHead>
                        <TableHead>Tarih</TableHead>
                        <TableHead>Ödeme</TableHead>
                        <TableHead className="text-right">Tutar</TableHead>
                        <TableHead className="text-center">İşlemler</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {expenses.map((expense) => {
                        const Icon = CATEGORY_ICONS[expense.category] || DotsThree;
                        return (
                          <TableRow key={expense.id} data-testid={`expense-row-${expense.id}`}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Icon className="w-4 h-4 text-muted-foreground" />
                                <span>{expense.category_label}</span>
                                {expense.is_recurring ? (
                                  <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-600 border-blue-500/20">Sabit</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-xs bg-orange-500/10 text-orange-600 border-orange-500/20">Günlük</Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {expense.description || '-'}
                            </TableCell>
                            <TableCell>{expense.date}</TableCell>
                            <TableCell>
                              <Badge variant="secondary" className="text-xs">
                                {expense.payment_type_label || 'Nakit'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right font-semibold">
                              {formatCurrency(expense.amount)}
                              {expense.is_recurring && (
                                <span className="text-xs text-muted-foreground ml-1">
                                  ({formatCurrency(expense.amount / 30)}/gün)
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex justify-center gap-2">
                                {isManager && (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleEditExpense(expense)}
                                    >
                                      <Pencil className="w-4 h-4" />
                                    </Button>
                                    {isAdmin && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDeleteExpense(expense.id)}
                                        className="text-destructive hover:text-destructive"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    )}
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {/* Total Row */}
                      <TableRow className="bg-muted/50 font-bold">
                        <TableCell colSpan={3}>TOPLAM</TableCell>
                        <TableCell className="text-right">{formatCurrency(monthTotal)}</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Charts Tab */}
          <TabsContent value="charts">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Pie Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Kategori Dağılımı</CardTitle>
                </CardHeader>
                <CardContent>
                  {pieData.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">Veri yok</div>
                  ) : (
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Bar Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Aylık Trend</CardTitle>
                </CardHeader>
                <CardContent>
                  {barData.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">Veri yok</div>
                  ) : (
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={barData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis 
                            dataKey="month" 
                            tick={{ fontSize: 11 }}
                            tickFormatter={(value) => value.slice(5)}
                          />
                          <YAxis 
                            tick={{ fontSize: 11 }}
                            tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                          />
                          <Tooltip 
                            formatter={(value) => formatCurrency(value)}
                            contentStyle={{
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px',
                            }}
                          />
                          <Bar dataKey="total_expense" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Sabit Gider Şablonları</CardTitle>
                  {isAdmin && (
                    <Dialog open={isTemplateDialogOpen} onOpenChange={setIsTemplateDialogOpen}>
                      <DialogTrigger asChild>
                        <Button size="sm" data-testid="add-template-btn">
                          <Plus className="w-4 h-4 mr-2" />
                          Şablon Ekle
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md">
                        <DialogHeader>
                          <DialogTitle>Yeni Şablon</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                          <div>
                            <Label>Kategori</Label>
                            <Select value={templateForm.category} onValueChange={(v) => setTemplateForm({ ...templateForm, category: v })}>
                              <SelectTrigger>
                                <SelectValue placeholder="Kategori seçin" />
                              </SelectTrigger>
                              <SelectContent>
                                {categories.map((cat) => (
                                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div>
                            <Label>Aylık Tutar (TL)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={templateForm.amount}
                              onChange={(e) => setTemplateForm({ ...templateForm, amount: e.target.value })}
                              placeholder="0.00"
                            />
                          </div>
                          
                          <div>
                            <Label>Açıklama (Opsiyonel)</Label>
                            <Input
                              value={templateForm.description}
                              onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                              placeholder="Açıklama..."
                            />
                          </div>
                          
                          <Button onClick={handleCreateTemplate} className="w-full">
                            Kaydet
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Sabit gider şablonları, her ay başında "Şablonları Uygula" butonuyla otomatik gider kaydı oluşturur.
                </p>
                
                {templates.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Henüz şablon oluşturulmamış
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Kategori</TableHead>
                        <TableHead>Açıklama</TableHead>
                        <TableHead className="text-right">Aylık Tutar</TableHead>
                        {isAdmin && <TableHead className="text-center">İşlem</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {templates.map((template) => {
                        const Icon = CATEGORY_ICONS[template.category] || DotsThree;
                        return (
                          <TableRow key={template.id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Icon className="w-4 h-4 text-muted-foreground" />
                                <span>{template.category_label}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {template.description || '-'}
                            </TableCell>
                            <TableCell className="text-right font-semibold">
                              {formatCurrency(template.amount)}
                            </TableCell>
                            {isAdmin && (
                              <TableCell>
                                <div className="flex justify-center">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteTemplate(template.id)}
                                    className="text-destructive hover:text-destructive"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                      {/* Total Row */}
                      <TableRow className="bg-muted/50 font-bold">
                        <TableCell colSpan={2}>AYLIK TOPLAM</TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(templates.reduce((sum, t) => sum + t.amount, 0))}
                        </TableCell>
                        {isAdmin && <TableCell></TableCell>}
                      </TableRow>
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Kategori Ekleme Dialog */}
      <Dialog open={isCategoryDialogOpen} onOpenChange={setIsCategoryDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Yeni Gider Kategorisi</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Kategori Kodu</Label>
              <Input
                value={newCategory.code}
                onChange={(e) => setNewCategory({ ...newCategory, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_') })}
                placeholder="Örn: TEMIZLIK"
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">Sadece büyük harf ve alt çizgi kullanın</p>
            </div>
            <div>
              <Label>Kategori Adı</Label>
              <Input
                value={newCategory.label}
                onChange={(e) => setNewCategory({ ...newCategory, label: e.target.value })}
                placeholder="Örn: Temizlik Malzemeleri"
                className="mt-1"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setIsCategoryDialogOpen(false)}>
                İptal
              </Button>
              <Button className="flex-1" onClick={handleCreateCategory}>
                Kategori Ekle
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
